import { Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import { formatMXN, productImageUrl } from '@/lib/api';
import { calcularIva, totalConIva } from '@/lib/precios';
import { EmptyState } from '@/components/ui';
import { cn } from '@/lib/utils';

export function CartDrawer() {
  const { items, isOpen, toggleCart, removeItem, updateQuantity, subtotal } =
    useCartStore();

  const base = subtotal();
  const iva = calcularIva(base);
  const totalConImpuesto = totalConIva(base);

  if (!isOpen) return null;

  return (
    <>
      <div className="drawer-overlay" onClick={() => toggleCart(false)} aria-hidden />
      <aside
        className={cn(
          'drawer-panel',
          'max-lg:animate-drawer-up lg:animate-drawer-in',
        )}
      >
        <div className="border-b border-surface-200 px-4 pb-3 pt-2 lg:px-5 lg:py-4">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-surface-200 lg:hidden" aria-hidden />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="hidden h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 lg:flex">
                <ShoppingBag className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-bold text-surface-950">Carrito</h2>
                {items.length > 0 && (
                  <p className="text-xs text-surface-500">{items.length} línea(s)</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleCart(false)}
              className="icon-btn icon-btn-ghost"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 lg:px-5 lg:py-4">
          {items.length === 0 ? (
            <EmptyState
              icon={<ShoppingBag className="h-6 w-6" />}
              title="Sin productos"
              description="Agrega equipos desde el catálogo."
              action={
                <Link to="/catalogo" onClick={() => toggleCart(false)} className="btn-primary">
                  Ir al catálogo
                </Link>
              }
              className="mt-4 border-none bg-transparent lg:mt-8"
            />
          ) : (
            <ul className="space-y-3">
              {items.map((item) => (
                <li key={item.id} className="flex gap-3 rounded-lg border border-surface-200 p-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-surface-100">
                    <img
                      src={productImageUrl(item.imagen)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-surface-900">
                          {item.nombre}
                        </p>
                        {item.esKit && <span className="badge-kit mt-1">Kit</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="shrink-0 text-surface-400 hover:text-red-600"
                        aria-label="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-0.5 text-sm tabular-nums">
                      <span className="font-semibold text-brand-700">{formatMXN(item.precio)}</span>
                      <span className="text-xs font-normal text-surface-500"> sin IVA</span>
                    </p>
                    <p className="text-[11px] text-surface-500">
                      + IVA · {formatMXN(totalConIva(item.precio * item.cantidad))}
                    </p>
                    <div className="mt-2 inline-flex items-center rounded-md border border-surface-200">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.cantidad - 1)}
                        className="flex h-8 w-8 items-center justify-center active:bg-surface-50"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[2rem] text-center text-sm font-medium tabular-nums">
                        {item.cantidad}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.cantidad + 1)}
                        className="flex h-8 w-8 items-center justify-center active:bg-surface-50"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-surface-200 bg-surface-50 px-4 py-3 lg:px-5 lg:py-4">
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-surface-600">
                <span>Subtotal (sin IVA)</span>
                <span className="tabular-nums font-medium text-surface-900">
                  {formatMXN(base)}
                </span>
              </div>
              <div className="flex justify-between text-surface-600">
                <span>IVA (16%)</span>
                <span className="tabular-nums font-medium text-surface-900">
                  {formatMXN(iva)}
                </span>
              </div>
              <div className="flex justify-between border-t border-surface-200 pt-2 text-base font-bold">
                <span className="text-surface-900">Total c/IVA</span>
                <span className="tabular-nums text-brand-700">{formatMXN(totalConImpuesto)}</span>
              </div>
            </div>
            <Link
              to="/checkout"
              onClick={() => toggleCart(false)}
              className="btn-primary mt-3 w-full py-3 lg:mt-4"
            >
              Checkout
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
